import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Award, Calendar, Lightbulb, X } from 'lucide-react';

/** Figma — BibleQuizScreen.tsx QUIZ_DESIGN 과 동일 */
const QUIZ_DESIGN = {
  contentMaxWidth: 326,
  pagePaddingX: 30,
  quizCardRadius: 20,
  quizCardPadding: 33,
  quizLabelSize: 18,
  quizLabelTracking: '0.6px',
  quizLabelLineHeight: '16px',
  quizQuestionSize: 24,
  quizQuestionLineHeight: '38px',
  quizLabelToQuestionGap: 16,
  dateToCardGap: 22,
  cardToOxGap: 30,
  oxToFeedbackGap: 58,
  feedbackGap: 10,
  feedbackEmojiSize: 60,
  feedbackTitleSize: 18,
  feedbackTitleLineHeight: '28px',
  feedbackBodySize: 18,
  feedbackBodyLineHeight: '24px',
  feedbackBodyMaxWidth: 294,
  oxButtonHeight: 100,
  oxButtonRadius: 20,
  oxButtonGap: 14,
  primaryButtonShadow: '0px 10px 15px -3px rgba(0, 0, 0, 0.1), 0px 4px 6px -4px rgba(0, 0, 0, 0.1)',
  primaryButtonPadding: '20px 32px',
  primaryButtonFontSize: 20,
  primaryButtonLineHeight: '24px',
  primaryButtonMarginTop: 48,
};

const QUIZ_COMPLETE_DESIGN = {
  cardRadius: 20,
  cardHeight: 156,
  cardPadding: 24,
  cardWidth: 155,
  topPadding: '48px 40px',
  topGap: 32,
  iconToLabelGap: 12,
  labelToValueGap: 4,
  levelCardPadding: '26px 24px',
  quoteMaxWidth: 326,
  quotePaddingY: 48,
  quoteGap: 16,
  ctaPaddingX: 32,
  ctaPaddingBottom: 32,
  awardSize: 64,
  titleSize: 28,
  titleLineHeight: '33.6px',
  labelSize: 16,
  streakValueSize: 22,
  streakValueLineHeight: '28.6px',
  levelValueSize: 16,
  levelValueLineHeight: '24px',
  quoteSize: 16,
  quoteLineHeight: '20px',
  exitButtonShadow: '0px 10px 15px -3px rgba(0,0,0,0.1), 0px 4px 6px -4px rgba(0,0,0,0.1)',
};

const INITIAL_QUIZ_DESIGN = {
  hintBox: {
    width: 35,
    height: 40,
    borderRadius: 10,
    fontSize: 28,
    fontWeight: 500,
    lineHeight: '26px',
    padding: 2,
  },
  boxGap: 10,
};

/** getBibleQuizColors(false) — 라이트 팔레트 */
const C = {
  pageBg: '#FFFFFF',
  quizCardBg: 'rgba(255, 240, 196, 0.3)',
  quizCardBorder: '#FFE088',
  quizLabelColor: '#5B4132',
  quizQuestionColor: '#000000',
  feedbackColor: '#EE9F0E',
  oxButtonBg: '#F1F1F1',
  oxSelectedBorder: '#05AF6F',
  oxIconStroke: '#05AF6F',
  oxXStroke: '#E53935',
  oxActiveXBg: '#FFFFFF',
  primaryButton: '#007167',
  primaryButtonText: '#FFFFFF',
  completeCardBg: '#fff0c4',
  completeCardShadow: '0 4px 12px rgba(93,64,55,0.04)',
  awardBg: '#FFE088',
  awardIcon: '#181d1a',
  titleColor: '#1e1b16',
  subtitleColor: '#454742',
  labelColor: '#454742',
  quoteColor: '#5d4037',
  exitButtonBg: '#3c6752',
  exitButtonText: '#FFFFFF',
  completeIconSurface: '#FFFFFF',
  hintBoxBorder: '2px solid rgba(0, 0, 0, 0.2)',
  hintBoxBg: '#FFFFFF',
  hintBoxColor: '#000000',
  initialPrimaryButton: '#3C6752',
  inputBorder: '#D1D5DB',
  inputBorderCorrect: '#10B981',
  inputBg: '#FFFFFF',
  inputBgCorrect: '#F3FCF8',
  inputText: '#111827',
  inputTextCorrect: '#059669',
  answerRevealBorder: '#10B981',
  answerRevealBg: '#F3FCF8',
  answerRevealText: '#059669',
  dateDecor: '#888888',
};

const WEEKDAY_LABELS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

const normalizeAnswer = (value) => String(value || '').trim().replace(/\s+/g, '').toLowerCase();

const parseDateParts = (raw) => {
  const trimmed = String(raw || '').trim();
  let year;
  let month;
  let day;
  if (/^\d{8}$/.test(trimmed)) {
    year = Number(trimmed.slice(0, 4));
    month = Number(trimmed.slice(4, 6));
    day = Number(trimmed.slice(6, 8));
  } else if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    const parts = trimmed.split('-').map(Number);
    year = parts[0];
    month = parts[1];
    day = parts[2];
  } else {
    const d = new Date();
    return { day: String(d.getDate()), weekday: WEEKDAY_LABELS[d.getDay()] };
  }
  const d = new Date(year, month - 1, day);
  return { day: String(d.getDate()), weekday: WEEKDAY_LABELS[d.getDay()] };
};

function OxChoiceIcon({ value }) {
  if (value === 'O') {
    return (
      <svg width="45" height="42" viewBox="0 0 45 42" fill="none" aria-hidden>
        <circle cx="22.5" cy="21" r="15" stroke={C.oxIconStroke} strokeWidth="6" />
      </svg>
    );
  }
  return (
    <svg width="45" height="42" viewBox="0 0 45 42" fill="none" aria-hidden>
      <path d="M13 11L32 31M32 11L13 31" stroke={C.oxXStroke} strokeWidth="6" strokeLinecap="round" />
    </svg>
  );
}

function PreviewDateHeader({ dateYmd }) {
  const { day, weekday } = useMemo(() => parseDateParts(dateYmd), [dateYmd]);
  return (
    <div style={{ position: 'relative', height: 66, width: '100%', filter: 'drop-shadow(0px 4px 4px rgba(0,0,0,0.02))' }}>
      <svg
        width={59}
        height={33}
        viewBox="0 0 60 33"
        fill="none"
        style={{ position: 'absolute', left: 0, top: 0, display: 'block' }}
        aria-hidden
      >
        <path
          d="M3.32513 14.2432V21.4346L22.8085 7.9668L23.0869 7.77344L23.3691 7.96094L59.8281 32.1982V24.5283L23.1005 0.601562L3.32513 14.2432Z"
          fill={C.dateDecor}
          stroke={C.dateDecor}
          strokeLinecap="square"
        />
        <path
          d="M3.82802 2.97754V17.4561M7.12738 6.24689H0.999992"
          stroke={C.dateDecor}
          strokeWidth="2"
          strokeLinecap="square"
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 31,
          height: 1,
          background: C.dateDecor,
        }}
      />
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          top: 31,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          paddingTop: 2,
        }}
      >
        <span style={{ fontSize: 32, lineHeight: '22px', fontWeight: 600, color: C.titleColor }}>{day}</span>
        <span style={{ fontSize: 10, lineHeight: '20px', color: C.dateDecor, letterSpacing: 1 }}>{weekday}</span>
      </div>
    </div>
  );
}

/**
 * missionqt-app BibleQuizScreen 과 동일한 UX·스타일로 퀴즈 미리보기
 * @param {{ quizSet: object, questions: object[], onClose: () => void }} props
 */
const QuizPreviewModal = ({ quizSet, questions: rawQuestions, onClose }) => {
  const questions = useMemo(
    () =>
      [...(rawQuestions || [])]
        .filter((q) => String(q.question_text || '').trim())
        .map((q, index) => ({
          ...q,
          question_no: Number(q.question_no) || index + 1,
          question_type: q.question_type === 'INITIAL' ? 'INITIAL' : 'OX',
        }))
        .sort((a, b) => (a.question_no || 0) - (b.question_no || 0)),
    [rawQuestions]
  );

  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOx, setSelectedOx] = useState(null);
  const [initialAnswer, setInitialAnswer] = useState('');
  const [checked, setChecked] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [correctCount, setCorrectCount] = useState(0);
  const [wrongGuideMessage, setWrongGuideMessage] = useState('');
  const [showEmptyAnswerGuide, setShowEmptyAnswerGuide] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const initialAnswerInputRef = useRef(null);

  const currentQuestion = questions[currentIndex] || null;
  const headerDateYmd = quizSet?.qt_ymd || quizSet?.quiz_date || '';

  const initialHintChars = useMemo(() => {
    if (!currentQuestion || currentQuestion.question_type !== 'INITIAL') return [];
    const raw = String(currentQuestion.initial_hint || '').replace(/\s+/g, '');
    if (raw) return Array.from(raw);
    return Array.from(String(currentQuestion.correct_answer || '').replace(/\s+/g, ''));
  }, [currentQuestion]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    if (!currentQuestion || currentQuestion.question_type !== 'INITIAL') {
      setInitialAnswer('');
      return;
    }
    setInitialAnswer('');
  }, [currentQuestion]);

  useEffect(() => {
    if (!currentQuestion || currentQuestion.question_type !== 'INITIAL' || checked) return;
    const t = window.setTimeout(() => initialAnswerInputRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [currentQuestion, checked]);

  const handleCheckAnswer = (forcedOxAnswer) => {
    if (!currentQuestion || checked) return;
    let userAnswer = '';
    if (currentQuestion.question_type === 'OX') {
      const answer = forcedOxAnswer ?? selectedOx;
      if (!answer) return;
      userAnswer = answer;
      if (forcedOxAnswer) setSelectedOx(forcedOxAnswer);
    } else {
      if (!initialAnswer.trim()) {
        setShowEmptyAnswerGuide(true);
        return;
      }
      userAnswer = initialAnswer;
    }

    const matched = normalizeAnswer(userAnswer) === normalizeAnswer(currentQuestion.correct_answer);
    setShowEmptyAnswerGuide(false);
    setChecked(true);
    setIsCorrect(matched);
    if (matched) {
      setCorrectCount((prev) => prev + 1);
      setWrongGuideMessage('');
    } else {
      setWrongGuideMessage(
        currentQuestion.explanation?.trim() ||
          currentQuestion.wrong_guide ||
          quizSet?.wrong_guide_default ||
          '정답을 다시 확인해보세요.'
      );
    }
  };

  const handleNext = () => {
    if (!checked) return;
    if (currentIndex >= questions.length - 1) {
      setShowSummary(true);
      return;
    }
    setCurrentIndex((prev) => prev + 1);
    setSelectedOx(null);
    setInitialAnswer('');
    setChecked(false);
    setIsCorrect(false);
    setWrongGuideMessage('');
    setShowEmptyAnswerGuide(false);
  };

  const canSubmitAnswer =
    currentQuestion?.question_type === 'OX' ? !!selectedOx : !!initialAnswer.trim();

  const resetPreview = () => {
    setCurrentIndex(0);
    setSelectedOx(null);
    setInitialAnswer('');
    setChecked(false);
    setIsCorrect(false);
    setCorrectCount(0);
    setWrongGuideMessage('');
    setShowEmptyAnswerGuide(false);
    setShowSummary(false);
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="퀴즈 미리보기"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1050,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 16,
      }}
      onClick={onClose}
    >
      <div
        style={{
          width: '100%',
          maxWidth: 420,
          maxHeight: '92vh',
          background: C.pageBg,
          borderRadius: 16,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 50px rgba(0,0,0,0.25)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더 — HeaderNew 유사 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 16px',
            borderBottom: '1px solid #eee',
            flexShrink: 0,
            background: '#fff',
          }}
        >
          <span style={{ fontSize: 16, fontWeight: 700, color: C.titleColor }}>오늘의 퀴즈 미리보기</span>
          <button
            type="button"
            className="btn btn-link p-0"
            onClick={onClose}
            aria-label="닫기"
            style={{ color: C.titleColor, lineHeight: 1 }}
          >
            <X size={22} />
          </button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1, background: C.pageBg }}>
          {questions.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: C.quizLabelColor }}>
              미리볼 문항이 없습니다. 문제 내용을 입력한 뒤 다시 시도해주세요.
            </div>
          ) : showSummary ? (
            <div className="d-flex flex-column" style={{ minHeight: '100%' }}>
              <div
                className="d-flex flex-column align-items-center text-center"
                style={{ padding: QUIZ_COMPLETE_DESIGN.topPadding, gap: QUIZ_COMPLETE_DESIGN.topGap }}
              >
                <div
                  className="d-inline-flex align-items-center justify-content-center rounded-circle"
                  style={{
                    width: QUIZ_COMPLETE_DESIGN.awardSize,
                    height: QUIZ_COMPLETE_DESIGN.awardSize,
                    backgroundColor: C.awardBg,
                    flexShrink: 0,
                  }}
                >
                  <Award size={32} style={{ color: C.awardIcon }} />
                </div>
                <div className="d-flex flex-column align-items-center" style={{ gap: 8 }}>
                  <h1
                    className="mb-0"
                    style={{
                      fontSize: QUIZ_COMPLETE_DESIGN.titleSize,
                      fontWeight: 600,
                      color: C.titleColor,
                      lineHeight: QUIZ_COMPLETE_DESIGN.titleLineHeight,
                    }}
                  >
                    퀴즈 완료!
                  </h1>
                  <p className="mb-0" style={{ fontSize: 14, color: C.subtitleColor, lineHeight: '20px' }}>
                    {questions.length}문제 중 {correctCount}문제 정답
                  </p>
                </div>
              </div>

              <div
                className="d-flex justify-content-between"
                style={{ margin: '0 30px', height: QUIZ_COMPLETE_DESIGN.cardHeight }}
              >
                <div
                  className="d-flex flex-column align-items-center justify-content-center text-center"
                  style={{
                    backgroundColor: C.completeCardBg,
                    borderRadius: QUIZ_COMPLETE_DESIGN.cardRadius,
                    boxShadow: C.completeCardShadow,
                    width: QUIZ_COMPLETE_DESIGN.cardWidth,
                    height: QUIZ_COMPLETE_DESIGN.cardHeight,
                    padding: QUIZ_COMPLETE_DESIGN.cardPadding,
                    flexShrink: 0,
                  }}
                >
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle"
                    style={{
                      width: 40,
                      height: 40,
                      backgroundColor: C.completeIconSurface,
                      marginBottom: QUIZ_COMPLETE_DESIGN.iconToLabelGap,
                    }}
                  >
                    <Calendar size={20} style={{ color: C.quoteColor }} />
                  </div>
                  <p
                    className="mb-0"
                    style={{
                      fontSize: QUIZ_COMPLETE_DESIGN.labelSize,
                      color: C.labelColor,
                      marginBottom: QUIZ_COMPLETE_DESIGN.labelToValueGap,
                    }}
                  >
                    연속 참석
                  </p>
                  <p
                    className="mb-0 fw-medium"
                    style={{
                      fontSize: QUIZ_COMPLETE_DESIGN.streakValueSize,
                      lineHeight: QUIZ_COMPLETE_DESIGN.streakValueLineHeight,
                      color: C.titleColor,
                    }}
                  >
                    —
                  </p>
                </div>
                <div
                  className="d-flex flex-column align-items-center justify-content-center text-center"
                  style={{
                    backgroundColor: C.completeCardBg,
                    borderRadius: QUIZ_COMPLETE_DESIGN.cardRadius,
                    boxShadow: C.completeCardShadow,
                    width: QUIZ_COMPLETE_DESIGN.cardWidth,
                    height: QUIZ_COMPLETE_DESIGN.cardHeight,
                    padding: QUIZ_COMPLETE_DESIGN.levelCardPadding,
                    flexShrink: 0,
                  }}
                >
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle"
                    style={{
                      width: 40,
                      height: 40,
                      backgroundColor: C.completeIconSurface,
                      marginBottom: QUIZ_COMPLETE_DESIGN.iconToLabelGap,
                    }}
                  >
                    <Award size={20} style={{ color: C.quoteColor }} />
                  </div>
                  <p
                    className="mb-0"
                    style={{
                      fontSize: QUIZ_COMPLETE_DESIGN.labelSize,
                      color: C.labelColor,
                      marginBottom: QUIZ_COMPLETE_DESIGN.labelToValueGap,
                    }}
                  >
                    레벨
                  </p>
                  <p
                    className="mb-0 fw-bold"
                    style={{
                      fontSize: QUIZ_COMPLETE_DESIGN.levelValueSize,
                      lineHeight: QUIZ_COMPLETE_DESIGN.levelValueLineHeight,
                      color: C.titleColor,
                    }}
                  >
                    미리보기
                  </p>
                </div>
              </div>

              <div
                className="d-flex flex-column align-items-center w-100"
                style={{ padding: `${QUIZ_COMPLETE_DESIGN.quotePaddingY}px 24px`, gap: QUIZ_COMPLETE_DESIGN.quoteGap }}
              >
                <p
                  className="mb-0 text-center"
                  style={{
                    maxWidth: QUIZ_COMPLETE_DESIGN.quoteMaxWidth,
                    fontSize: QUIZ_COMPLETE_DESIGN.quoteSize,
                    lineHeight: QUIZ_COMPLETE_DESIGN.quoteLineHeight,
                    color: C.quoteColor,
                  }}
                >
                  {quizSet?.title ? `「${quizSet.title}」` : '미리보기 모드입니다.'}
                </p>
              </div>

              <div
                className="mt-auto"
                style={{
                  paddingLeft: QUIZ_COMPLETE_DESIGN.ctaPaddingX,
                  paddingRight: QUIZ_COMPLETE_DESIGN.ctaPaddingX,
                  paddingBottom: QUIZ_COMPLETE_DESIGN.ctaPaddingBottom,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                }}
              >
                <button
                  type="button"
                  className="btn w-100 border-0 rounded-pill fw-bold"
                  onClick={resetPreview}
                  style={{
                    backgroundColor: C.primaryButton,
                    color: C.primaryButtonText,
                    fontSize: 18,
                    lineHeight: '24px',
                    padding: '16px 28px',
                    boxShadow: QUIZ_COMPLETE_DESIGN.exitButtonShadow,
                  }}
                >
                  다시 미리보기
                </button>
                <button
                  type="button"
                  className="btn w-100 border-0 rounded-pill fw-bold"
                  onClick={onClose}
                  style={{
                    backgroundColor: C.exitButtonBg,
                    color: C.exitButtonText,
                    fontSize: 18,
                    lineHeight: '24px',
                    padding: '16px 28px',
                    boxShadow: QUIZ_COMPLETE_DESIGN.exitButtonShadow,
                  }}
                >
                  닫기
                </button>
              </div>
            </div>
          ) : (
            <div style={{ maxWidth: 390, margin: '0 auto', padding: `0 ${QUIZ_DESIGN.pagePaddingX}px 40px` }}>
              <div style={{ paddingTop: 8 }}>
                {headerDateYmd ? <PreviewDateHeader dateYmd={headerDateYmd} /> : null}
              </div>

              {currentQuestion && (
                <div style={{ maxWidth: QUIZ_DESIGN.contentMaxWidth, margin: '0 auto' }}>
                  <div
                    style={{
                      marginTop: QUIZ_DESIGN.dateToCardGap,
                      background: C.quizCardBg,
                      border: `1px solid ${C.quizCardBorder}`,
                      borderRadius: QUIZ_DESIGN.quizCardRadius,
                      padding: QUIZ_DESIGN.quizCardPadding,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        marginBottom: QUIZ_DESIGN.quizLabelToQuestionGap,
                        color: C.quizLabelColor,
                        fontSize: QUIZ_DESIGN.quizLabelSize,
                        fontWeight: 400,
                        letterSpacing: QUIZ_DESIGN.quizLabelTracking,
                        lineHeight: QUIZ_DESIGN.quizLabelLineHeight,
                      }}
                    >
                      <Lightbulb size={17} strokeWidth={1.75} aria-hidden />
                      <span>Quiz {currentQuestion.question_no}</span>
                    </div>
                    <div
                      style={{
                        fontSize: QUIZ_DESIGN.quizQuestionSize,
                        fontWeight: 400,
                        color: C.quizQuestionColor,
                        lineHeight: QUIZ_DESIGN.quizQuestionLineHeight,
                        wordBreak: 'break-word',
                      }}
                    >
                      {currentQuestion.question_text}
                    </div>
                    {currentQuestion.question_type === 'INITIAL' && initialHintChars.length > 0 && (
                      <div
                        style={{
                          marginTop: 16,
                          display: 'flex',
                          gap: INITIAL_QUIZ_DESIGN.boxGap,
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        {initialHintChars.map((char, index) => (
                          <div
                            key={`hint-${index}-${char}`}
                            style={{
                              ...INITIAL_QUIZ_DESIGN.hintBox,
                              border: C.hintBoxBorder,
                              background: C.hintBoxBg,
                              color: C.hintBoxColor,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              boxSizing: 'border-box',
                            }}
                          >
                            {char}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {currentQuestion.question_type === 'OX' ? (
                    <div
                      style={{
                        marginTop: QUIZ_DESIGN.cardToOxGap,
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: QUIZ_DESIGN.oxButtonGap,
                      }}
                    >
                      {['O', 'X'].map((value) => {
                        const active = selectedOx === value;
                        return (
                          <button
                            key={value}
                            type="button"
                            className="btn d-flex align-items-center justify-content-center"
                            disabled={checked}
                            onClick={() => handleCheckAnswer(value)}
                            aria-pressed={active}
                            style={{
                              height: QUIZ_DESIGN.oxButtonHeight,
                              borderRadius: QUIZ_DESIGN.oxButtonRadius,
                              border: active ? `2px solid ${C.oxSelectedBorder}` : '2px solid transparent',
                              background: active && value === 'X' ? C.oxActiveXBg : C.oxButtonBg,
                              padding: 0,
                              boxShadow: 'none',
                            }}
                          >
                            <OxChoiceIcon value={value} />
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ marginTop: QUIZ_DESIGN.cardToOxGap }}>
                      <input
                        ref={initialAnswerInputRef}
                        type="text"
                        value={
                          checked ? (isCorrect ? currentQuestion.correct_answer : initialAnswer) : initialAnswer
                        }
                        readOnly={checked}
                        tabIndex={checked ? -1 : 0}
                        placeholder="정답을 적어주세요!"
                        onChange={(event) => {
                          setInitialAnswer(event.target.value);
                          if (event.target.value.trim() && showEmptyAnswerGuide) {
                            setShowEmptyAnswerGuide(false);
                          }
                        }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' && !checked) {
                            event.preventDefault();
                            handleCheckAnswer();
                          }
                        }}
                        style={{
                          width: '100%',
                          height: 52,
                          borderRadius: 12,
                          border: checked
                            ? isCorrect
                              ? `2px solid ${C.inputBorderCorrect}`
                              : `1px solid ${C.inputBorder}`
                            : `1px solid ${C.inputBorder}`,
                          background: checked && isCorrect ? C.inputBgCorrect : C.inputBg,
                          textAlign: 'center',
                          fontSize: checked && isCorrect ? 22 : 18,
                          fontWeight: 700,
                          color: checked && isCorrect ? C.inputTextCorrect : C.inputText,
                          padding: '0 16px',
                          outline: 'none',
                          boxSizing: 'border-box',
                        }}
                      />
                      {checked && !isCorrect && (
                        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'center' }}>
                          <div
                            style={{
                              width: '100%',
                              padding: '10px 20px',
                              borderRadius: 12,
                              border: `1.5px solid ${C.answerRevealBorder}`,
                              color: C.answerRevealText,
                              fontWeight: 800,
                              fontSize: 22,
                              lineHeight: 1.2,
                              textAlign: 'center',
                              background: C.answerRevealBg,
                              boxSizing: 'border-box',
                            }}
                          >
                            {currentQuestion.correct_answer}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {checked && (
                    <div
                      style={{
                        marginTop: QUIZ_DESIGN.oxToFeedbackGap,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: QUIZ_DESIGN.feedbackGap,
                        textAlign: 'center',
                      }}
                    >
                      <span style={{ fontSize: QUIZ_DESIGN.feedbackEmojiSize, lineHeight: 1 }} aria-hidden>
                        {isCorrect ? '🙌' : '😢'}
                      </span>
                      <p
                        className="mb-0"
                        style={{
                          fontSize: QUIZ_DESIGN.feedbackTitleSize,
                          fontWeight: 500,
                          lineHeight: QUIZ_DESIGN.feedbackTitleLineHeight,
                          color: C.feedbackColor,
                        }}
                      >
                        {isCorrect ? '정답이에요!' : '아쉽네요, 오답입니다.'}
                      </p>
                      {!isCorrect && (
                        <p
                          className="mb-0"
                          style={{
                            maxWidth: QUIZ_DESIGN.feedbackBodyMaxWidth,
                            fontSize: QUIZ_DESIGN.feedbackBodySize,
                            fontWeight: 500,
                            lineHeight: QUIZ_DESIGN.feedbackBodyLineHeight,
                            color: C.feedbackColor,
                            whiteSpace: 'pre-line',
                            wordBreak: 'break-word',
                            textAlign: 'left',
                          }}
                        >
                          {wrongGuideMessage}
                        </p>
                      )}
                    </div>
                  )}

                  <button
                    type="button"
                    className="btn w-100"
                    onClick={checked ? handleNext : () => handleCheckAnswer()}
                    disabled={!checked && !canSubmitAnswer && !showEmptyAnswerGuide}
                    style={{
                      marginTop: QUIZ_DESIGN.primaryButtonMarginTop,
                      padding: QUIZ_DESIGN.primaryButtonPadding,
                      borderRadius: 9999,
                      border: 'none',
                      background:
                        currentQuestion.question_type === 'INITIAL'
                          ? C.initialPrimaryButton
                          : C.primaryButton,
                      color: C.primaryButtonText,
                      fontWeight: 700,
                      fontSize: QUIZ_DESIGN.primaryButtonFontSize,
                      lineHeight: QUIZ_DESIGN.primaryButtonLineHeight,
                      boxShadow: QUIZ_DESIGN.primaryButtonShadow,
                      opacity: !checked && !canSubmitAnswer ? 0.3 : 1,
                    }}
                  >
                    {checked
                      ? currentIndex === questions.length - 1
                        ? '결과 보기'
                        : '다음'
                      : showEmptyAnswerGuide
                        ? '정답을 적어주세요!'
                        : currentQuestion.question_type === 'OX'
                          ? '다음'
                          : '정답 확인'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuizPreviewModal;
